import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetCurriculumManagementQuery,
  useUpdateCurriculumManagementMutation,
} from "../../../../../../../../services/school";
import type {
  CurriculumGrade,
  Module,
  SubModule,
  TlmTopic,
} from "../../../../../../../../services/school/crm/curriculum/useGetCurriculumManagementQuery";
import { toast } from "sonner";

export const useGradeManagementInfo = () => {
  const { id: schoolId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [curriculumData, setCurriculumData] = useState<CurriculumGrade[]>([]);
  const [expandedGrades, setExpandedGrades] = useState<Set<string>>(new Set());
  const [expandedModules, setExpandedModules] = useState<Set<string>>(
    new Set()
  );
  const [expandedSubModules, setExpandedSubModules] = useState<Set<string>>(
    new Set()
  );

  const {
    data: curriculumResponse,
    isLoading,
    error,
    refetch: refetchCurriculumData,
  } = useGetCurriculumManagementQuery(
    { schoolId: schoolId || "" },
    { enabled: !!schoolId }
  );

  const updateMutation = useUpdateCurriculumManagementMutation({
    onSuccess: (data: any) => {
      // Invalidate and refetch curriculum data to get the latest state
      toast.success("Curriculum updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["grade-management"],
        exact: false,
      });
      // Also directly refetch the data to ensure it's updated
      refetchCurriculumData();
    },
    onError: (error: any) => {
      console.error("Error updating curriculum:", error);
      toast.error(error?.response?.data?.issues?.error);
    },
  });

  useEffect(() => {
    if (curriculumResponse?.data) {
      setCurriculumData(curriculumResponse.data?.classes || []);
    }
  }, [curriculumResponse]);

  const handleGradeCheckboxChange = (gradeId: string, checked: boolean) => {
    setCurriculumData((prev) =>
      prev.map((grade) =>
        grade.class_id === gradeId
          ? {
              ...grade,
              isEligible: checked,
              modules: grade.modules.map((module) => ({
                ...module,
                isEligible: checked,
                sub_modules: module.sub_modules.map((sub) => ({
                  ...sub,
                  isEligible: checked,
                  tlm_topics: sub.tlm_topics.map((topic) => ({
                    ...topic,
                    isEligible: checked,
                  })),
                })),
              })),
            }
          : grade
      )
    );
  };

  const handleModuleCheckboxChange = (
    gradeId: string,
    moduleId: string,
    checked: boolean
  ) => {
    setCurriculumData((prev) =>
      prev.map((grade) => {
        if (grade.class_id !== gradeId) return grade;

        const modules = grade.modules.map((module) =>
          module.module_id === moduleId
            ? {
                ...module,
                isEligible: checked,
                sub_modules: module.sub_modules.map((sub) => ({
                  ...sub,
                  isEligible: checked,
                  tlm_topics: sub.tlm_topics.map((topic) => ({
                    ...topic,
                    isEligible: checked,
                  })),
                })),
              }
            : module
        );

        return {
          ...grade,
          isEligible: modules.some((m) => m.isEligible),
          modules,
        };
      })
    );
  };

  const handleSubModuleCheckboxChange = (
    gradeId: string,
    moduleId: string,
    subModuleId: string,
    checked: boolean
  ) => {
    setCurriculumData((prev) =>
      prev.map((grade) => {
        if (grade.class_id !== gradeId) return grade;

        const modules = grade.modules.map((module) => {
          if (module.module_id !== moduleId) return module;

          const sub_modules = module.sub_modules.map((sub) =>
            sub.sub_module_id === subModuleId
              ? {
                  ...sub,
                  isEligible: checked,
                  tlm_topics: sub.tlm_topics.map((topic) => ({
                    ...topic,
                    isEligible: checked,
                  })),
                }
              : sub
          );

          return {
            ...module,
            isEligible: sub_modules.some((s) => s.isEligible),
            sub_modules,
          };
        });

        return {
          ...grade,
          isEligible: modules.some((m) => m.isEligible),
          modules,
        };
      })
    );
  };

  const handleTlmTopicCheckboxChange = (
    gradeId: string,
    moduleId: string,
    subModuleId: string,
    topicId: string,
    checked: boolean
  ) => {
    setCurriculumData((prev) =>
      prev.map((grade) => {
        if (grade.class_id !== gradeId) return grade;

        const modules = grade.modules.map((module) => {
          if (module.module_id !== moduleId) return module;

          const sub_modules = module.sub_modules.map((sub) => {
            if (sub.sub_module_id !== subModuleId) return sub;

            const tlm_topics = sub.tlm_topics.map((topic) =>
              topic.tlmMasterId === topicId
                ? { ...topic, isEligible: checked }
                : topic
            );

            return {
              ...sub,
              isEligible: tlm_topics.some((t) => t.isEligible),
              tlm_topics,
            };
          });

          return {
            ...module,
            isEligible: sub_modules.some((s) => s.isEligible),
            sub_modules,
          };
        });

        return {
          ...grade,
          isEligible: modules.some((m) => m.isEligible),
          modules,
        };
      })
    );
  };

  const saveChanges = () => {
      console.log({ schoolId, curriculumData });
    if (schoolId && curriculumData.length > 0) {
      // Transform data to match API payload structure
      const transformedData = {
        data: {
          classes: curriculumData
            .filter((grade) => grade.isEligible) // Only include selected grades
            .map((grade) => ({
              class_id: grade.class_id,
              curriculum_id: grade.curriculum.curriculum_id,
              modules: grade.modules
                .filter((module) => module.isEligible) // Only include eligible modules
                .map((module) => ({
                  module_id: module.module_id,
                  sub_modules: module.sub_modules
                    .filter((subModule) => subModule.isEligible) // Only include eligible sub-modules
                    .map((subModule) => ({
                      sub_module_id: subModule.sub_module_id,
                      tlm_topics: subModule.tlm_topics
                        .filter((topic) => topic.isEligible) // Only include eligible TLM topics
                        .map((topic) => ({
                          tlmMasterId: topic.tlmMasterId,
                        })),
                    })),
                })),
            })),
        },
      };
      console.log({ transformedData });
      
      updateMutation.mutate({
        schoolId,
        data: transformedData as any,
      });
    } else {
      console.log("Cannot save - missing schoolId or curriculumData:", {
        schoolId,
        curriculumDataLength: curriculumData.length,
      });
    }
  };

  const toggleGradeExpansion = (gradeId: string) => {
    const newExpanded = new Set(expandedGrades);
    if (newExpanded.has(gradeId)) {
      newExpanded.delete(gradeId);
    } else {
      newExpanded.add(gradeId);
    }
    setExpandedGrades(newExpanded);
  };

  const toggleModuleExpansion = (gradeId: string, moduleId: string) => {
    const compositeKey = `${gradeId}-${moduleId}`;
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(compositeKey)) {
      newExpanded.delete(compositeKey);
    } else {
      newExpanded.add(compositeKey);
    }
    setExpandedModules(newExpanded);
  };

  const toggleSubModuleExpansion = (
    gradeId: string,
    moduleId: string,
    subModuleId: string
  ) => {
    const compositeKey = `${gradeId}-${moduleId}-${subModuleId}`;
    const newExpanded = new Set(expandedSubModules);
    if (newExpanded.has(compositeKey)) {
      newExpanded.delete(compositeKey);
    } else {
      newExpanded.add(compositeKey);
    }
    setExpandedSubModules(newExpanded);
  };

  const collapseAll = () => {
    setExpandedGrades(new Set());
    setExpandedModules(new Set());
    setExpandedSubModules(new Set());
  };

  return {
    // State
    curriculumData,
    expandedGrades,
    expandedModules,
    expandedSubModules,
    isLoading,
    error,

    // Handlers
    handleGradeCheckboxChange,
    handleModuleCheckboxChange,
    handleSubModuleCheckboxChange,
    handleTlmTopicCheckboxChange,
    toggleGradeExpansion,
    toggleModuleExpansion,
    toggleSubModuleExpansion,
    collapseAll,
    saveChanges,
  };
};
